/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion -- DIRECT-239681 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { defaultSerializeQueryArgs, skipToken } from '@reduxjs/toolkit/query/react';
import type { Draft } from '@reduxjs/toolkit';
import { stableSerialize, useIsomorphicLayoutEffect, values, AnyFn, Optional } from '@direct-frontend/stdlib';
import type { LegacyDataProvider } from '@adv-frontend/data-provider';
import type { Patch } from '../immer-types';

import type { AnyQueryDefinition, QueryEndpointMethodsAndHooks, UseQueryStateDefaultResult } from '../apiSSRModule';
import { isTagsEqual } from '../invalidationTagsUtils';
import type { QueryArgFrom, ResultTypeFrom, SkipToken, TagDescription } from '../rtk-query-types';

import { BaseRowData, RequestedChunks, UseEndpointDataProviderPaginator } from './types';
import { createStaticRowsGenerator } from './createStaticRowsGenerator';
import { createDynamicRowsGenerator } from './createDynamicRowsGenerator';
import { memoizeTransformChunk } from './lib/memoizeTransformChunk/memoizeTransformChunk';

/**
 * Тип возвращаемых данных из endpoint-датапровайдера.
 * @deprecated Используйте flow-based {@link EndpointDataProvider}.
 */
export interface LegacyEndpointDataProvider<
    Definition extends AnyQueryDefinition = AnyQueryDefinition,
    RowData extends BaseRowData = BaseRowData,
> extends LegacyDataProvider<RowData> {
    /**
     * Выполняется ли загрузка данных для первого чанка.
     */
    isLoading: boolean;
    /**
     * Выполняется ли обновление данных для первого чанка.
     */
    isFetching: boolean;
    /**
     * Возвращает список закешированных в датапровайдере значений
     */
    getCachedValues: () => UseQueryStateDefaultResult<Definition>[];
    /**
     * Возвращает параметры запроса для строки.
     * @param rowIndex Номер строки.
     * @returns Параметры запроса или skipToken.
     */
    getRowQueryArg: (rowIndex: number) => QueryArgFrom<Definition> | SkipToken;
}

/**
 * Базовая конфигурация провайдера.
 */
type BaseEndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = {
    /** Стратегия получения параметров окна для запросов эндпоинта */
    usePaginatorFactory: () => UseEndpointDataProviderPaginator<Definition, PaginatorParams>;
    /** Функция получения параметров запроса эндпоинта */
    getQueryArg(
        paginatorParams: PaginatorParams,
        queryArg: QueryArg,
        cachedValues: UseQueryStateDefaultResult<Definition>[]
    ): QueryArgFrom<Definition> | SkipToken;
    /**
     * Функция подготовки данных чанка: вычисляется один раз на чанк, а ее результат приходит
     * первым аргументом в `getRowByIndex` вместо сырого ответа эндпоинта. Сюда выносится все,
     * что одинаково для всех строк чанка — например списки идентификаторов для батч-запросов.
     *
     * Результат обязан держать объекты чанка по ссылке, а не копировать их: `updateRowDataManual`
     * вызывает трансформацию на immer-черновике, и мутации копий в черновик не попадут.
     */
    transformChunk?(chunkData: ResultTypeFrom<Definition>, queryArg: QueryArg): TransformedChunk;
    /**
     * Функция получения строки из подготовленных данных чанка (из ответа эндпоинта, если `transformChunk` не передан).
     * Третьим аргументом получает queryArg провайдера — строке может понадобиться, например,
     * чтобы построить вложенный поток за дополнительными данными по параметрам запроса.
     */
    getRowByIndex(chunkData: NoInfer<TransformedChunk>, rowIndexInChunk: number, queryArg: QueryArg): Optional<RowData>;
    /**
     * Функция получения списка тегов, которые приводят к инвалидации строки.
     * Если передана, то провайдер переключается в режим построчной инвалидации данных. При построчной инвалидации,
     * в случае инвалидации чанка, в строке флаг `isFetching` будет равен `true` только если чанк был инвалидирован
     * из-за тега этой строки.
     */
    getRowInvalidatedBy?(row: RowData): TagDescription<string>[];
};

/**
 * Конфигурация провайдера для фиксированной генерации строк.
 */
type EndpointDataProviderWithTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk> & {
    /**
     * Функция получения общего количества строк из ответа эндпоинта
     */
    getTotalCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Конфигурация провайдера для динамической генерации строк.
 */
type EndpointDataProviderWithoutTotalCountConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> = BaseEndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk> & {
    /**
     * Функция получения количества строк в чанке из ответа эндпоинта
     */
    getRowsCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Конфигурация провайдера.
 */
type EndpointDataProviderConfig<
    Definition extends AnyQueryDefinition,
    PaginatorParams extends object,
    RowData extends BaseRowData,
    QueryArg,
    TransformedChunk,
> =
    | EndpointDataProviderWithTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>
    | EndpointDataProviderWithoutTotalCountConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>;

/**
 * Thunk обновляющий данные чанка
 */
export type UpdateQueryData<EndpointName extends string, Definition extends AnyQueryDefinition> = (
    /** Название эндпоинта */
    name: EndpointName,
    /** queryArg соответствующие чанку */
    queryArg: QueryArgFrom<Definition>,
    /** Функция обновляющая данные сущности */
    updateRecipe: (data: Draft<ResultTypeFrom<Definition>>) => void
) => {
    /** Метод, возвращающий данные в исходное состояние */
    undo: () => void;
    /** Набор immer-патчей, которые произошли внутри `updateRecipe` */
    patches: Patch[];
    /** Набор обратных immer-патчей */
    inversePatches: Patch[];
};

/**
 * Параметры для хука, возвращаемого методом createLegacyDataProvider.
 */
type EndpointDataProviderOptions = {
    /**
     * Индекс первой строки.
     * Важно указывать строку, которая отрендерится на сервере.
     */
    initialRowIndex?: number;
};

/**
 * Тип, который описывает параметр `createLegacyDataProvider` для query-эндпоинта.
 * @deprecated Используйте flow-based {@link EndpointCreateDataProvider}.
 */
export type LegacyEndpointCreateDataProvider<Definition extends AnyQueryDefinition> = {
    createLegacyDataProvider: <
        PaginatorParams extends object,
        RowData extends BaseRowData,
        QueryArg,
        TransformedChunk = ResultTypeFrom<Definition>,
    >(
        config: EndpointDataProviderConfig<Definition, PaginatorParams, RowData, QueryArg, TransformedChunk>
    ) => (
        ...[queryArg]: undefined extends QueryArg
            ? [queryArg?: QueryArg, options?: EndpointDataProviderOptions]
            : [queryArg: QueryArg, options?: EndpointDataProviderOptions]
    ) => LegacyEndpointDataProvider<Definition, RowData>;
};

/**
 * Выводит тип данных строки в endpoint-датапровайдере.
 * @deprecated Используйте {@link InferEndpointDataProviderRowData} для flow-based провайдера.
 */
export type InferLegacyEndpointDataProviderRowData<T extends AnyFn> =
    ReturnType<T> extends LegacyEndpointDataProvider<any, infer RowData> ? RowData : never;

function isDataProviderWithTotalCountConfig<Definition extends AnyQueryDefinition, RowData extends BaseRowData>(
    config: EndpointDataProviderConfig<Definition, any, RowData, any, any>
): config is EndpointDataProviderWithTotalCountConfig<Definition, any, RowData, any, any> {
    return 'getTotalCount' in config;
}

/**
 * Метод для генерации createLegacyDataProvider метода эндпойнта.
 * @deprecated Используйте flow-based {@link buildCreateEndpointDataProvider}.
 */
export function buildCreateLegacyEndpointDataProvider<
    EndpointName extends string,
    Definition extends AnyQueryDefinition,
>(
    endpoint: {
        name: EndpointName;
        subscribe: QueryEndpointMethodsAndHooks<Definition>['subscribe'];
        useQuery: QueryEndpointMethodsAndHooks<Definition>['useQuery'];
        useQueryState: QueryEndpointMethodsAndHooks<Definition>['useQueryState'];
        useCachedValues: QueryEndpointMethodsAndHooks<Definition>['useCachedValues'];
        updateQueryData: QueryEndpointMethodsAndHooks<Definition>['updateQueryData'];
    },
    definition: Definition
): LegacyEndpointCreateDataProvider<Definition>['createLegacyDataProvider'] {
    function serializeEndpointQueryArg(endpointQueryArg: QueryArgFrom<Definition> | typeof skipToken): string {
        const serializeResult = definition.serializeQueryArgs?.({
            queryArgs: endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });

        // Если `serializeQueryArgs` возвращает строку, то используем ее как ключ
        if (typeof serializeResult === 'string') {
            return serializeResult;
        }

        // Иначе используем дефолтный rtk-query сериализатор
        return defaultSerializeQueryArgs({
            // Передаем результат `serializeQueryArgs` если функция определена,
            // либо все queryArgs если не определена
            queryArgs: serializeResult ?? endpointQueryArg,
            endpointDefinition: definition,
            endpointName: endpoint.name,
        });
    }

    // Создание датапровайдера, источником данных для которого является эндпойнт.
    return function createEndpointDataProvider(config) {
        const {
            usePaginatorFactory: createUsePaginator,
            getQueryArg,
            getRowByIndex,
            getRowInvalidatedBy,
            transformChunk,
        } = config;

        type TransformedChunk = Parameters<typeof getRowByIndex>[0];

        const getTransformedChunk = transformChunk ? memoizeTransformChunk(transformChunk) : undefined;

        const useRowsGenerator = isDataProviderWithTotalCountConfig(config)
            ? createStaticRowsGenerator({
                  getTotalCount: config.getTotalCount,
              })
            : createDynamicRowsGenerator({ getRowsCount: config.getRowsCount });

        return function useDataProvider(...[queryArg, { initialRowIndex = 0 } = {}]) {
            const usePaginator = useMemo(() => createUsePaginator(), []);

            // Стабилизируем значение queryArg,
            // чтобы делать новые запросы только если параметры запроса действительно изменилось.
            const stableQueryArgString = useMemo(() => stableSerialize(queryArg), [queryArg]);
            // eslint-disable-next-line react-hooks/exhaustive-deps -- Стабилизируем ссылку через сериализацию в JSON.
            const stableQueryArg = useMemo(() => queryArg, [stableQueryArgString]);

            // Подготовка данных чанка (один раз на чанк) плюс сборка строки.
            const getRowByIndexWithQueryArg = useCallback(
                (chunkData: ResultTypeFrom<Definition>, rowIndexInChunk: number) => {
                    if (!getTransformedChunk) {
                        // Без transformChunk строка получает сырой ответ эндпоинта: в этом случае TransformedChunk
                        // равен ResultTypeFrom<Definition>, но внутри дженерика это не выводится.
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                        return getRowByIndex(chunkData as TransformedChunk, rowIndexInChunk, stableQueryArg!);
                    }

                    return getRowByIndex(
                        getTransformedChunk(chunkData, stableQueryArg!),
                        rowIndexInChunk,
                        stableQueryArg!
                    );
                },
                [stableQueryArg]
            );

            const initialPaginatorParams = useMemo(() => ({ requestedChunks: [], cachedValues: [] }), []);
            const initialPaginator = usePaginator(initialPaginatorParams);
            // Параметры начального запроса.
            const initialEndpointQueryArg = useMemo(
                () => getQueryArg(initialPaginator.getChunkParams(initialRowIndex), stableQueryArg!, []),
                [initialPaginator, initialRowIndex, stableQueryArg]
            );

            // Запоминаем параметры запроса и начальный индекс для всех чанков.
            // Чанки могут загружаться не последовательно.
            // Это позволяет получить все запрошенные провайдером данные ниже.
            const [requestedChunks, setRequestedChunks] = useState<RequestedChunks<Definition>>(
                initialEndpointQueryArg === skipToken
                    ? []
                    : [
                          {
                              queryArg: initialEndpointQueryArg,
                              serializedQueryArg: serializeEndpointQueryArg(initialEndpointQueryArg),
                              startIndex: initialRowIndex,
                          },
                      ]
            );

            // Получаем все запрошенные провайдером данные, чтобы сформировать список всех строк в провайдере.
            // Список всех строк в провайдере (в том числе и не загруженных) нужен, например, для react-table.
            const cachedValues = endpoint.useCachedValues(...requestedChunks.map(({ queryArg }) => queryArg));
            const paginator = usePaginator({ requestedChunks, cachedValues });

            // При первом вызове хука запрашиваем первый чанк,
            // общее количество строк и все доступные для выделения id.
            const queryResult = endpoint.useQuery(initialEndpointQueryArg);
            const { data, error } = queryResult;
            // Если первый чанк инвалидируется и включена построчная инвалидация, то в `isFetching` всего
            // провайдера всегда отдаем `false`, чтобы определять этот флаг внутри строк.
            const isFetching = queryResult.invalidatedBy && getRowInvalidatedBy ? false : queryResult.isFetching;

            const rowsGenerator = useRowsGenerator(paginator, {
                getRowByIndex: getRowByIndexWithQueryArg,
                requestedChunks,
                cachedValues,
                initialData: data,
            });

            // В случае ошибки запроса первого чанка выбрасываем её наружу,
            // чтобы поймать в ErrorBoundary.
            if (error) {
                throw error;
            }

            // Провайдер сам управляет подписками на данные, чтобы делать это более эффективно.
            // Создается одна подписка на чанк, вместо подписки для каждой строки.
            // Поэтому храним актуальный список подписок самостоятельно.
            const subscriptionsRef = useRef<Record<string, Function>>({});

            const rows = useMemo(() => rowsGenerator.getRows(), [rowsGenerator]);
            const loadedRows = useMemo(
                () =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- разные дженерики для типа RowData
                    rowsGenerator.getLoadedRows() as InferLegacyEndpointDataProviderRowData<
                        LegacyEndpointCreateDataProvider<Definition>['createLegacyDataProvider']
                    >,
                [rowsGenerator]
            );
            const totalCount = rowsGenerator.getTotalCount();

            // При смене параметров запроса или анмаунте провайдера – очищаем подписки и
            // список использованных параметров. Подписки очищаются в dataProvider,
            // а не в самих строках, так как строки могут быть виртуализированными и
            // не замаунченными в данный момент.
            useIsomorphicLayoutEffect(() => {
                return () => {
                    for (const unsubscribe of values(subscriptionsRef.current)) {
                        unsubscribe();
                    }

                    subscriptionsRef.current = {};
                    setRequestedChunks([]);
                };
            }, [stableQueryArg]);

            return useMemo(() => {
                return {
                    isFetching,
                    isLoading: false,
                    totalCount,
                    getRows: () => rows,
                    getLoadedRows: () => loadedRows,
                    getCachedValues: () => cachedValues,
                    getRowQueryArg: rowIndex => {
                        const chunkParams = paginator.getChunkParams(rowIndex);
                        return getQueryArg(chunkParams, stableQueryArg!, cachedValues);
                    },
                    updateRowDataManual: (
                        rowIndex: number,
                        updateRecipe: (rowData: NonNullable<ReturnType<typeof getRowByIndex>>) => void
                    ) => {
                        const chunkParams = paginator.getChunkParams(rowIndex);
                        const endpointQueryArg = getQueryArg(chunkParams, stableQueryArg!, cachedValues);

                        const { undo } = endpoint.updateQueryData(
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                            endpointQueryArg as QueryArgFrom<Definition>,
                            chunkData => {
                                const rowIndexInChunk = paginator.getRowIndexInChunk(rowIndex, chunkParams);
                                const data = chunkData
                                    ? getRowByIndexWithQueryArg(chunkData, rowIndexInChunk)
                                    : undefined;

                                if (data) {
                                    updateRecipe(data);
                                }
                            }
                        );

                        return { undo };
                    },
                    useRowData(indexes: number | number[]) {
                        const rowIndex = Array.isArray(indexes) ? (indexes[0] ?? 0) : indexes;
                        const chunkParams = useMemo(
                            () => paginator.getChunkParams(rowIndex),
                            // Параметр `paginator` необходим для корректной работы хука
                            // eslint-disable-next-line react-hooks/exhaustive-deps -- см. выше
                            [rowIndex, paginator]
                        );

                        const endpointQueryArg = useMemo(
                            () => getQueryArg(chunkParams, stableQueryArg!, cachedValues),
                            // Параметр `stableQueryArg` необходим для корректной работы хука
                            // eslint-disable-next-line react-hooks/exhaustive-deps -- см. выше
                            [chunkParams, stableQueryArg]
                        );

                        const serializedQueryArg = useMemo(
                            () => serializeEndpointQueryArg(endpointQueryArg),
                            [endpointQueryArg]
                        );

                        const rowIndexInChunk = useMemo(
                            () => paginator.getRowIndexInChunk(rowIndex, chunkParams),
                            [rowIndex, chunkParams]
                        );

                        // 1. Вызываем запрос и создаем подписку, если её еще нет.
                        // 2. Запоминаем параметры запроса, чтобы позже провайдер мог сформировать полный список строк.
                        // Используем useLayoutEffect, чтобы все обновления стейта привели к синхронному ререндеру.
                        useIsomorphicLayoutEffect(() => {
                            const shouldSubscribe =
                                !subscriptionsRef.current[serializedQueryArg] && endpointQueryArg !== skipToken;

                            if (shouldSubscribe) {
                                subscriptionsRef.current[serializedQueryArg] = endpoint.subscribe(endpointQueryArg);

                                const chunkStartIndex = rowIndex - rowIndexInChunk;
                                const hasRequestedChunk = requestedChunks.some(
                                    chunk => chunk.serializedQueryArg === serializedQueryArg
                                );

                                if (!hasRequestedChunk) {
                                    setRequestedChunks(state => [
                                        ...state,
                                        {
                                            queryArg: endpointQueryArg,
                                            serializedQueryArg,
                                            startIndex: chunkStartIndex,
                                        },
                                    ]);
                                }
                            }
                        }, [rowIndex, rowIndexInChunk, endpointQueryArg, serializedQueryArg]);

                        const {
                            data: chunkData,
                            error,
                            invalidatedBy: chunkInvalidatedBy,
                            isError,
                            isFetching,
                            isLoading,
                            isUninitialized,
                        } = endpoint.useQueryState(endpointQueryArg);

                        // В случае ошибки запроса выбрасываем её наружу, чтобы поймать в ErrorBoundary.
                        if (error) {
                            throw error;
                        }

                        const willStartLoadingInEffect =
                            isUninitialized && !subscriptionsRef.current[serializedQueryArg];
                        const data = chunkData ? getRowByIndexWithQueryArg(chunkData, rowIndexInChunk) : undefined;

                        // Если чанк инвалидируется и включена построчная инвалидация,
                        // то в `isFetching = true`, если среди тегов чанка есть хотя бы один тег строки.
                        const rowIsFetching =
                            chunkInvalidatedBy && getRowInvalidatedBy
                                ? chunkInvalidatedBy.some(chunkTag => {
                                      const rowInvalidatedBy = data ? getRowInvalidatedBy(data) : [];
                                      return rowInvalidatedBy.some(rowTag => isTagsEqual(chunkTag, rowTag));
                                  })
                                : isFetching;

                        return {
                            data,
                            isLoading: isLoading || willStartLoadingInEffect,
                            isFetching: rowIsFetching,
                            isError,
                        };
                    },
                };
            }, [
                isFetching,
                totalCount,
                rows,
                cachedValues,
                paginator,
                stableQueryArg,
                loadedRows,
                requestedChunks,
                getRowByIndexWithQueryArg,
            ]);
        };
    };
}
